import { useEffect } from "react";

const BASE_TITLE = "TP Software";
const BASE_DOMAIN = "https://app.tp.software";

const upsertMeta = (selector, attrName, attrValue, contentAttr, contentValue) => {
  if (contentValue == null) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute(contentAttr, contentValue);
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * useSeo
 *
 * Sets document.title, meta description, canonical URL, Open Graph and Twitter
 * tags for the current route. All content should already be translated before
 * being passed in.
 *
 * @param {object} opts
 * @param {string} opts.title        Page title (without the " — TP Software" suffix)
 * @param {string} [opts.description]  Meta description (under ~160 chars)
 * @param {string} [opts.keywords]     Comma-separated keywords
 * @param {string} [opts.path]         Canonical path (e.g. "/terms-condition")
 * @param {string} [opts.image]        Absolute URL for OG image
 * @param {string} [opts.type="website"]  OG type
 * @param {string} [opts.locale]       Locale code (e.g. "pt_PT")
 * @param {boolean} [opts.noindex]     If true, sets robots=noindex
 */
export const useSeo = ({
  title,
  description,
  keywords,
  path,
  image,
  type = "website",
  locale,
  noindex = false,
} = {}) => {
  useEffect(() => {
    const fullTitle = title
      ? title.endsWith(BASE_TITLE)
        ? title
        : `${title} — ${BASE_TITLE}`
      : `${BASE_TITLE} — Gestão de Centros de Abate Automóvel`;

    document.title = fullTitle;

    // Description
    if (description) {
      upsertMeta('meta[name="description"]', "name", "description", "content", description);
      upsertMeta('meta[property="og:description"]', "property", "og:description", "content", description);
      upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", "content", description);
    }

    // Keywords
    if (keywords) {
      upsertMeta('meta[name="keywords"]', "name", "keywords", "content", keywords);
    }

    // Robots
    upsertMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      "content",
      noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"
    );

    // Canonical + OG URL
    const canonical = path ? `${BASE_DOMAIN}${path}` : undefined;
    if (canonical) {
      upsertLink("canonical", canonical);
      upsertMeta('meta[property="og:url"]', "property", "og:url", "content", canonical);
    }

    // OG title + Twitter title
    upsertMeta('meta[property="og:title"]', "property", "og:title", "content", fullTitle);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", "content", fullTitle);

    // OG type
    upsertMeta('meta[property="og:type"]', "property", "og:type", "content", type);

    // OG locale
    if (locale) {
      upsertMeta('meta[property="og:locale"]', "property", "og:locale", "content", locale);
    }

    // OG image + Twitter image
    if (image) {
      upsertMeta('meta[property="og:image"]', "property", "og:image", "content", image);
      upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", "content", image);
    }

    // Fire GA4 page_view on every route change (after SPA navigation)
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_title: fullTitle,
        page_location: window.location.href,
        page_path: path || window.location.pathname,
      });
    }
  }, [title, description, keywords, path, image, type, locale, noindex]);
};

export default useSeo;

// Map i18n language code → OG locale
export const i18nToOgLocale = (lang) => {
  const map = {
    pt: "pt_PT",
    en: "en_US",
    es: "es_ES",
    fr: "fr_FR",
    de: "de_DE",
    it: "it_IT",
  };
  return map[(lang || "pt").substring(0, 2)] || "pt_PT";
};

// Update <html lang="..."> whenever the i18n language changes
export const syncHtmlLang = (lang) => {
  if (!lang) return;
  const code = lang.substring(0, 2);
  if (document.documentElement.getAttribute("lang") !== code) {
    document.documentElement.setAttribute("lang", code);
  }
};
